import { HttpClient } from '@angular/common/http';
import * as i0 from '@angular/core';
import { InjectionToken, inject, Injectable, makeEnvironmentProviders } from '@angular/core';
import { EnvironmentService } from '@tc/foundation';
import { AuthenticationService } from '@tc/security-oauth-legacy';
import { ReplaySubject, combineLatest, iif, of } from 'rxjs';
import { first, switchMap, tap } from 'rxjs/operators';

/**
 * Injection token for the account settings model/configuration.
 * This is the default values object provided when configuring the module.
 */
const AccountSettingsModelToken = new InjectionToken('AccountSettingsModelToken');

/** @dynamic */
class AccountSettingsService {
    constructor() {
        this.http = inject(HttpClient);
        this.envService = inject(EnvironmentService);
        this.authService = inject(AuthenticationService);
        this.settingsSubject = new ReplaySubject(1);
        this.settings$ = this.settingsSubject.asObservable();
        const accountSettings = inject(AccountSettingsModelToken);
        this.accountSettings = accountSettings;
        this.initialize();
    }
    initialize() {
        combineLatest([this.envService.configuration$, this.authService.isAuthenticated$]).pipe(first(), switchMap(([configuration, isAuthenticated]) => iif(() => isAuthenticated, this.http.get(`${configuration.accountSettingsApiUrl ? configuration.accountSettingsApiUrl : configuration.apiUrl}/GetAccountSettings`), of({}))), tap(response => {
            const accountSettings = Object.assign({}, this.accountSettings);
            if (response.result != null && response.result.length > 0) {
                Object.keys(accountSettings).forEach(key => {
                    const setting = response.result.find(r => r.key === key);
                    if (setting != null) {
                        accountSettings[key] = this.convertValueFromString(setting);
                    }
                });
            }
            this.accountSettings = accountSettings;
            this.settingsSubject.next(this.accountSettings);
        })).toPromise();
    }
    set(key, value) {
        const jsonAccountSetting = {
            key: key,
            value: this.convertValueToString(value)
        };
        combineLatest([this.envService.configuration$, this.authService.isAuthenticated$]).pipe(first(), switchMap(([configuration, isAuthenticated]) => {
            const settings = Object.assign({}, this.accountSettings);
            settings[key] = value;
            this.accountSettings = settings;
            this.settingsSubject.next(this.accountSettings);
            if (isAuthenticated) {
                return this.http.put(`${configuration.accountSettingsApiUrl
                    ? configuration.accountSettingsApiUrl
                    : configuration.apiUrl}/UpsertAccountSetting`, jsonAccountSetting);
            }
            return of(jsonAccountSetting);
        })).toPromise();
    }
    convertValueFromString(setting) {
        if (setting != null && setting.value != null) {
            const key = setting.key;
            if (typeof this.accountSettings[key] === 'boolean') {
                return (setting.value === 'true');
            }
            else if (typeof this.accountSettings[key] === 'number') {
                return (parseInt(setting.value, 10));
            }
            else if (typeof this.accountSettings[key] === 'string') {
                return setting.value;
            }
            else {
                return JSON.parse(setting.value);
            }
        }
        return undefined;
    }
    convertValueToString(value) {
        if (typeof value === 'string') {
            return value;
        }
        else if (typeof value === 'number') {
            return value.toString();
        }
        else if (typeof value === 'boolean') {
            return value ? 'true' : 'false';
        }
        return JSON.stringify(value);
    }
    static { this.ɵfac = function AccountSettingsService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AccountSettingsService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AccountSettingsService, factory: AccountSettingsService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AccountSettingsService, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], () => [], null); })();

/**
 * Provides the AccountSettingsService with the specified default settings.
 *
 * This is the modern Angular standalone API for configuring the account settings service.
 * Use this in your application configuration instead of importing the module.
 *
 * @example
 * ```typescript
 * // In app.config.ts (Angular 20+ standalone apps) - Using a class
 * import { ApplicationConfig } from '@angular/core';
 * import { provideAccountSettings } from '@tc/account-settings';
 *
 * class MyAppSettings implements IAccountSettingsConfiguration {
 *   apiUrl = '';
 *   applicationName = 'my-app';
 *   configurationName = 'production';
 *   production = true;
 *   theme: 'light' | 'dark' = 'light';
 *   notifications = true;
 * }
 *
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideAccountSettings(MyAppSettings),
 *     // ... other providers
 *   ]
 * };
 * ```
 *
 * @example
 * ```typescript
 * // In main.ts (Angular 20+ standalone apps)
 * import { bootstrapApplication } from '@angular/platform-browser';
 * import { provideAccountSettings } from '@tc/account-settings';
 *
 * bootstrapApplication(AppComponent, {
 *   providers: [
 *     provideAccountSettings(MySettingsClass)
 *   ]
 * });
 * ```
 *
 * @param settingsModel The settings class constructor (will be instantiated automatically)
 * @returns Environment providers for the AccountSettingsService
 */
function provideAccountSettings(settingsModel) {
    return makeEnvironmentProviders([
        {
            provide: AccountSettingsModelToken,
            useValue: new settingsModel(),
        },
    ]);
}

class AccountSettingsServiceMock {
    constructor(settings) {
        this.settings = settings;
        this.settingsSubject = new ReplaySubject(1);
        this.settings$ = this.settingsSubject.asObservable();
        this.settingsSubject.next(settings);
    }
    set(key, value) {
        this.settings[key] = value;
        this.settingsSubject.next(this.settings);
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { AccountSettingsService, AccountSettingsServiceMock, provideAccountSettings };

